import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { AuthContextType, User, LoginCredentials, SignupData } from '../types/auth';
import api, { setAuthToken, setLogoutHandler } from '../services/api';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check for existing session on app load
    const savedToken = localStorage.getItem('accessToken');
    const savedUser = localStorage.getItem('user');
    
    if (savedToken && savedUser) {
      try {
        const userData = JSON.parse(savedUser);
        setUser(userData);
        setAuthToken(savedToken); // Set token in API client
      } catch (error) {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('user');
        localStorage.removeItem('token');
      }
    }
    
    // Set up logout handler for API interceptor
    setLogoutHandler(() => {
      setUser(null);
      localStorage.removeItem('accessToken');
      localStorage.removeItem('user');
      localStorage.removeItem('token');
    });
    
    setIsLoading(false);
  }, []);

  const login = async (credentials: LoginCredentials): Promise<void> => {
    setIsLoading(true);
    
    try {
      const response = await api.post('/auth/login', credentials);
      const data = response.data;
      
      // Backend returns 'token' but we use 'accessToken' in frontend
      const accessToken = data.token || data.accessToken;
      
      setUser(data.user);
      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('user', JSON.stringify(data.user));
      setAuthToken(accessToken); // Set token in API client
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const signup = async (signupData: SignupData): Promise<void> => {
    setIsLoading(true);
    
    try {
      const response = await api.post('/auth/signup', signupData);
      const data = response.data;
      
      // Backend returns 'token' but we use 'accessToken' in frontend
      const accessToken = data.token || data.accessToken;
      
      setUser(data.user);
      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('user', JSON.stringify(data.user));
      setAuthToken(accessToken); // Set token in API client
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Signup failed');
    } finally {
      setIsLoading(false);
    }
  };

  const logout = (): void => {
    setUser(null);
    localStorage.removeItem('accessToken');
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    setAuthToken(null); // Clear token from API client
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    signup,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

